import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from "react";

import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import {
    ScreenDictionary,
    ScreenList
} from "../utils/screen-manager/jira-screen-dictionary";
import {
    ProvisioningScreenKey,
    ScreenConfig,
    getVisibility,
    getConfiguredScreenList
} from "../utils/screen-manager/utils";

import { useRegisterMetadata } from "./use-registered-metadata";
export type ScreenManagerContext = {
    screensConfig?: ScreenConfig;
    screenDictionary?: ScreenDictionary;
    activeScreen?: ProvisioningScreenKey;
    screenList: ProvisioningScreenKey[];
    prevScreen: () => void;
    prevScreenExists?: () => boolean;
    nextScreen: () => void;
    skipScreen: () => void;
};
const ScreenManagerContext = createContext<ScreenManagerContext>({
    screensConfig: undefined,
    screenDictionary: undefined,
    activeScreen: undefined,
    screenList: [],
    prevScreen: () => console.warn("prevScreen not defined"),
    prevScreenExists: () => false,
    nextScreen: () => console.warn("nextScreen not defined"),
    skipScreen: () => console.warn("skipScreen not defined")
});
export const useScreenManager = (): ScreenManagerContext => {
    const context = useContext(ScreenManagerContext);
    if (context === undefined) {
        throw new Error(
            "`useScreenManager` must be used within a `ScreenManagerContext.Provider`"
        );
    }
    return context;
};

export type ScreenManagerProviderProps = {
    screensConfig: ScreenConfig;
    screenDictionary: ScreenDictionary;
} & React.PropsWithChildren;

/**
 * ScreenManagerProvider - provider to create screens to be viewed based on the screensConfig and metadata.
 *
 * @param screensConfig the configuration for screens that should be viewed in a partiicular flow.
 * @param screenDictionary the dictionary of supported screens and their keys.
 * @param children the children
 */
export const ScreenManagerProvider = ({
    screensConfig,
    screenDictionary,
    children
}: ScreenManagerProviderProps) => {
    const metadata = useRegisterMetadata();
    const skippedScreens = useMemo(() => new Set<ProvisioningScreenKey>(), []);
    const [screenIndex, setScreenIndex] = useState(0);
    const screenList = useMemo(
        () =>
            getConfiguredScreenList({
                metadata,
                screensConfig: screensConfig,
                fullScreenList: Object.keys(screenDictionary) as ScreenList
            }),
        [screensConfig, screenDictionary]
    );
    const activeScreen = useMemo(() => {
        const provisioningScreenKey = screenList[screenIndex];

        if (
            !checkFeatureFlagGate(
                featureFlags.confirmationPageInlineScreenConfigEval
            )
        ) {
            return provisioningScreenKey;
        }

        if (!provisioningScreenKey) {
            return undefined;
        }

        const screen = screensConfig[provisioningScreenKey];

        if (getVisibility(metadata, screen?.visibility)) {
            return provisioningScreenKey;
        }

        // skip this screen and go to the next one.
        skippedScreens.add(provisioningScreenKey);
        setScreenIndex((prevScreenIndex) => prevScreenIndex + 1);
        return provisioningScreenKey;
    }, [screenList, screenIndex]);

    const nextScreen = useCallback(() => {
        setScreenIndex(screenIndex + 1);
    }, [setScreenIndex, screenIndex]);

    const prevScreen = useCallback(() => {
        let updatedScreenIndex = Math.max(screenIndex - 1, 0);
        let updatedScreen = screenList[updatedScreenIndex];

        // find the previous screen the users should actually see.
        while (updatedScreen && skippedScreens.has(updatedScreen)) {
            skippedScreens.delete(updatedScreen);
            updatedScreenIndex = Math.max(updatedScreenIndex - 1, 0);
            updatedScreen = screenList[updatedScreenIndex];
        }

        setScreenIndex(updatedScreenIndex);
    }, [setScreenIndex, screenIndex]);

    const prevScreenExists = useCallback(() => {
        if (screenIndex === 0) {
            return false;
        }
        let updatedScreenIndex = screenIndex - 1;
        let updatedScreen = screenList[updatedScreenIndex];
        const skippedScreensCopy = new Set(skippedScreens); // deep copy

        while (updatedScreen && skippedScreens.has(updatedScreen)) {
            skippedScreensCopy.delete(updatedScreen);
            updatedScreenIndex = updatedScreenIndex - 1;
            if (updatedScreenIndex < 0) {
                break;
            }
            updatedScreen = screenList[updatedScreenIndex];
        }
        return updatedScreenIndex > -1;
    }, [setScreenIndex, screenIndex]);

    const skipScreen = useCallback(() => {
        if (activeScreen) {
            skippedScreens.add(activeScreen);
            nextScreen();
        }
    }, [nextScreen]);

    return (
        <ScreenManagerContext.Provider
            value={{
                screensConfig,
                screenDictionary,
                activeScreen,
                screenList,
                nextScreen,
                prevScreen,
                prevScreenExists,
                skipScreen
            }}
        >
            {children}
        </ScreenManagerContext.Provider>
    );
};
